"use client"

import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import type { Brand } from "@/lib/graphql/brands"

interface BrandFilterProps {
  brands: Brand[]
  /** Currently selected brand id, or "" when no brand filter is active. */
  activeBrand: string
}

/**
 * Brand dropdown used in the catalog toolbar (Shop All + category pages).
 *
 * Replaces the generic attribute filter (color/size/etc.) on the storefront:
 * those attributes carry option values in Bagisto's default catalog even when no
 * product uses them, which surfaced irrelevant filters. Brand is the only catalog
 * facet we filter on, so we render a single dedicated control next to the sort
 * selector instead of a sidebar full of unused attribute dropdowns.
 *
 * Mirrors SortSelector's URL scheme: writes `?brand=<id>` and resets pagination.
 */
export function BrandFilter({ brands, activeBrand }: BrandFilterProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations("filters")

  // Nothing to filter on when the tenant has no brands configured.
  if (brands.length === 0) {
    return null
  }

  function handleChange(value: string) {
    const params = new URLSearchParams(searchParams.toString())
    if (value) {
      params.set("brand", value)
    } else {
      params.delete("brand")
    }
    // Reset pagination when the brand filter changes
    params.delete("page")
    const qs = params.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
  }

  return (
    <select
      aria-label={t("brand")}
      value={activeBrand}
      onChange={(e) => handleChange(e.target.value)}
      className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <option value="">{t("allBrands")}</option>
      {brands.map((brand) => (
        <option key={brand.id} value={brand.id}>
          {brand.label}
        </option>
      ))}
    </select>
  )
}
